import React from "react";

/**
 * Full-screen "no internet" state for the creator portal. Shown when the
 * browser goes offline so uploads / actions fail gracefully. Matches the
 * portal's dark theme.
 */
type OfflineNoticeProps = {
  onRetry?: () => void;
};

export default function OfflineNotice({ onRetry }: OfflineNoticeProps) {
  const handleRetry = () => {
    if (onRetry) {
      onRetry();
      return;
    }
    if (typeof navigator === "undefined" || navigator.onLine) {
      window.location.reload();
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-[#0d0d0d] px-6 text-center text-white">
      <div className="flex w-full max-w-[420px] flex-col items-center">
        <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full border border-white/15 bg-white/5">
          <svg
            className="h-9 w-9 text-white/80"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M1 1l22 22" />
            <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
            <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
            <path d="M10.71 5.05A16 16 0 0 1 22.58 9" />
            <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
            <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
            <line x1="12" y1="20" x2="12.01" y2="20" />
          </svg>
        </div>

        <h1 className="mb-3 text-2xl font-bold sm:text-[28px]">You're offline</h1>

        <p className="mb-8 text-sm leading-relaxed text-white/70 sm:text-[15px]">
          We couldn't reach Vastavik because your device isn't connected to the
          internet. Check your connection and try again — your upload will
          resume once you're back online.
        </p>

        <button
          type="button"
          onClick={handleRetry}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-white px-8 py-3 text-[15px] font-medium text-black transition hover:bg-gray-200"
        >
          <svg
            className="h-4 w-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M23 4v6h-6" />
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
          </svg>
          Try Again
        </button>
      </div>
    </div>
  );
}
